<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ContactAPI from 'dashboard/api/contacts';
import CarListingsAPI from 'dashboard/api/wgpt/carListings';

const { t } = useI18n();
const router = useRouter();

const selectedContacts = ref([]);
const searchResults = ref([]);
const comboValue = ref('');
const defaultCurrency = ref('');
const defaultMileageUnit = ref('');
const socialCaptionTemplate = ref('');
const socialCaptionTemplateDefault = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

const NOT_SET = '';

const currencyOptions = computed(() => [
  { value: NOT_SET, label: t('WGPT_CAR_LISTINGS.SETTINGS.DEFAULTS.NOT_SET') },
  { value: 'usd', label: 'USD' },
  { value: 'eur', label: 'EUR' },
  { value: 'jod', label: 'JOD' },
  { value: 'sar', label: 'SAR' },
  { value: 'aed', label: 'AED' },
]);

const mileageUnitOptions = computed(() => [
  { value: NOT_SET, label: t('WGPT_CAR_LISTINGS.SETTINGS.DEFAULTS.NOT_SET') },
  { value: 'km', label: 'km' },
  { value: 'mi', label: 'mi' },
]);

const contactLabel = contact =>
  contact.name?.trim() ||
  contact.email ||
  contact.phone_number ||
  `#${contact.id}`;

const selectedIds = computed(() =>
  selectedContacts.value.map(contact => contact.id)
);

const contactOptions = computed(() =>
  searchResults.value
    .filter(contact => !selectedIds.value.includes(contact.id))
    .map(contact => ({
      value: contact.id,
      label: contactLabel(contact),
    }))
);

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await CarListingsAPI.getSettings();
    selectedContacts.value = data.contacts || [];
    defaultCurrency.value = data.defaults?.currency || '';
    defaultMileageUnit.value = data.defaults?.mileage_unit || '';
    socialCaptionTemplate.value = data.social_caption_template || '';
    socialCaptionTemplateDefault.value =
      data.social_caption_template_default || '';
  } catch (error) {
    useAlert(t('WGPT_CAR_LISTINGS.SETTINGS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = async term => {
  if (!term) {
    searchResults.value = [];
    return;
  }
  try {
    const { data } = await ContactAPI.search(term);
    searchResults.value = data.payload || [];
  } catch (error) {
    searchResults.value = [];
  }
};

const handleSelect = value => {
  if (!value) return;
  const contact = searchResults.value.find(item => item.id === value);
  if (contact && !selectedIds.value.includes(contact.id)) {
    selectedContacts.value.push({
      id: contact.id,
      name: contact.name,
      email: contact.email,
      phone_number: contact.phone_number,
    });
  }
  comboValue.value = '';
  searchResults.value = [];
};

const handleRemove = id => {
  selectedContacts.value = selectedContacts.value.filter(
    contact => contact.id !== id
  );
};

const handleSave = async () => {
  isSaving.value = true;
  try {
    await CarListingsAPI.updateSettings({
      contact_ids: selectedIds.value,
      defaults: {
        currency: defaultCurrency.value,
        mileage_unit: defaultMileageUnit.value,
      },
      social_caption_template: socialCaptionTemplate.value,
    });
    useAlert(t('WGPT_CAR_LISTINGS.SETTINGS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('WGPT_CAR_LISTINGS.SETTINGS.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

const handleBack = () => {
  router.push({ name: 'wgpt_car_listings_index' });
};

onMounted(fetchSettings);
</script>

<template>
  <div class="flex flex-col flex-1 w-full max-w-3xl gap-6 p-6 mx-auto">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h1 class="text-2xl font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.HEADER_TITLE') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.SUBTITLE') }}
        </span>
      </div>
      <Button
        :label="t('WGPT_CAR_LISTINGS.SETTINGS.BACK')"
        icon="i-lucide-arrow-left"
        variant="faded"
        color="slate"
        size="sm"
        @click="handleBack"
      />
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.CONTACTS_LABEL') }}
        </label>
        <span class="text-xs text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.CONTACTS_HINT') }}
        </span>
        <ComboBox
          v-model="comboValue"
          :options="contactOptions"
          use-api-results
          :placeholder="t('WGPT_CAR_LISTINGS.SETTINGS.SEARCH_PLACEHOLDER')"
          :search-placeholder="
            t('WGPT_CAR_LISTINGS.SETTINGS.SEARCH_PLACEHOLDER')
          "
          :empty-state="t('WGPT_CAR_LISTINGS.SETTINGS.SEARCH_EMPTY')"
          @search="handleSearch"
          @update:model-value="handleSelect"
        />
      </div>

      <div
        v-if="selectedContacts.length"
        class="flex flex-col gap-2 border rounded-xl border-n-weak divide-y divide-n-weak"
      >
        <div
          v-for="contact in selectedContacts"
          :key="contact.id"
          class="flex items-center justify-between gap-3 px-4 py-3"
        >
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ contactLabel(contact) }}
            </span>
            <span
              v-if="contact.email || contact.phone_number"
              class="text-xs truncate text-n-slate-11"
            >
              {{ contact.email || contact.phone_number }}
            </span>
          </div>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="xs"
            @click="handleRemove(contact.id)"
          />
        </div>
      </div>

      <div
        v-else-if="!isLoading"
        class="flex flex-col items-center justify-center gap-2 py-10 text-center border border-dashed rounded-xl border-n-weak"
      >
        <span class="text-3xl i-lucide-user-round-cog text-n-slate-9" />
        <span class="text-sm text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.EMPTY') }}
        </span>
      </div>

      <div class="flex flex-col gap-2 pt-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.DEFAULTS.LABEL') }}
        </label>
        <span class="text-xs text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.DEFAULTS.HINT') }}
        </span>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.SETTINGS.DEFAULTS.CURRENCY') }}
            </span>
            <ComboBox v-model="defaultCurrency" :options="currencyOptions" />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.SETTINGS.DEFAULTS.MILEAGE_UNIT') }}
            </span>
            <ComboBox
              v-model="defaultMileageUnit"
              :options="mileageUnitOptions"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2 pt-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.SOCIAL_CAPTION.LABEL') }}
        </label>
        <span class="text-xs text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SETTINGS.SOCIAL_CAPTION.HINT') }}
        </span>
        <TextArea
          v-model="socialCaptionTemplate"
          :placeholder="
            socialCaptionTemplateDefault ||
            t('WGPT_CAR_LISTINGS.SETTINGS.SOCIAL_CAPTION.PLACEHOLDER')
          "
          custom-text-area-class="!outline !outline-0"
          :max-length="2000"
          show-character-count
          auto-height
        />
      </div>

      <div class="flex justify-end">
        <Button
          :label="t('WGPT_CAR_LISTINGS.SETTINGS.SAVE')"
          :is-loading="isSaving"
          size="sm"
          @click="handleSave"
        />
      </div>
    </div>
  </div>
</template>
